import {
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import React from "react";
import { NavLink, useNavigate } from "react-router";
import { useRouter } from "../context/RouterContext";
import { useUserContext } from "../context/UserContext";
interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  variant?: "permanent" | "persistent" | "temporary";
}

const sidebarWidth = 280;

const menuItems = [
  {
    id: "vendite",
    label: "Vendite",
    icon: <DashboardIcon />,
    page: "vendite" as const,
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  variant = "persistent",
}) => {
  const { isLoading } = useUserContext();

  const filteredMenuItems = isLoading ? [] : menuItems;
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const { currentPage } = useRouter();
  const navigate = useNavigate();
  const handleItemClick = (
    itemId: string,
    hasChildren: boolean,
    page?: string,
  ) => {
    if (hasChildren) {
      setExpandedItems((prev) =>
        prev.includes(itemId)
          ? prev.filter((id) => id !== itemId)
          : [...prev, itemId],
      );
    } else if (page) {
      return navigate(page as any);
    }
  };

  const renderMenuItem = (item: any, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);

    const Wrapper = item.page && !hasChildren ? NavLink : React.Fragment;
    const wrapperProps =
      item.page && !hasChildren ? { to: `/${item.page}` } : {};

    return (
      <Wrapper key={item.id} {...(wrapperProps as any)}>
        <ListItem disablePadding sx={{ pl: level * 2 }}>
          <ListItemButton
            selected={
              item.page && !hasChildren
                ? currentPage === item.page
                : expandedItems.includes(item.id)
            }
            onClick={() => handleItemClick(item.id, hasChildren, item.page)}
            sx={{
              minHeight: 48,
              px: 2.5,
              "&:hover": {
                backgroundColor: "primary.light",
                color: "white !important",
                "& .MuiListItemIcon-root": {
                  color: "white !important",
                },
                "& .MuiListItemText-primary": {
                  color: "white !important",
                },
              },
              "&.Mui-selected": {
                backgroundColor: "#666666",
                color: "white !important",
                "& .MuiListItemIcon-root": {
                  color: "white !important",
                },
                "& .MuiListItemText-primary": {
                  color: "white !important",
                },
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 3,
                justifyContent: "center",
                color:
                  (item.page && !hasChildren && currentPage === item.page) ||
                  expandedItems.includes(item.id)
                    ? "white"
                    : "text.primary",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight:
                  (item.page && !hasChildren && currentPage === item.page) ||
                  expandedItems.includes(item.id)
                    ? 600
                    : 500,
                color:
                  (item.page && !hasChildren && currentPage === item.page) ||
                  expandedItems.includes(item.id)
                    ? "white"
                    : "text.primary",
              }}
            />
            {hasChildren && (isExpanded ? null : null)}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <List component="div" disablePadding>
            {item.children.map((child: any) =>
              renderMenuItem(child, level + 1),
            )}
          </List>
        )}
      </Wrapper>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        overflow: "auto",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header della Sidebar */}
      <Box
        sx={{
          p: 3,
          color: "white",
          textAlign: "center",
        }}
      >
        <div className="sidebar-logo">
          <img src="/fracomina_logo.avif" alt="Fracomina - Sellout Logo" />
        </div>
      </Box>

      <Divider />

      {/* Menu Navigation */}
      <Box sx={{ flexGrow: 1, py: 1 }}>
        <List>{filteredMenuItems.map((item) => renderMenuItem(item))}</List>
      </Box>

      <Divider />
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={isOpen}
      onClose={onToggle}
      sx={{
        width: 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: sidebarWidth,
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "fixed",
          zIndex: 999,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
